import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

export default function Contact() {
  const hours = useMemo(
    () => [
      { day: "Monday", time: "9am - 6pm" },
      { day: "Tuesday", time: "9am - 6pm" },
      { day: "Wednesday", time: "9am - 6pm" },
      { day: "Thursday", time: "9am - 6pm" },
      { day: "Friday", time: "9am - 6pm" },
      { day: "Saturday", time: "9am - 6pm" },
      { day: "Sunday", time: "Closed" },
    ],
    [],
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });

  const onChange = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
  };

  const address =
    "Unit 2, Beechwood House, Milnrow Road, Rochdale, Manchester OL16 2AA";
  const phone = "+44 7486 160965";
  const email = "info@kinewsstudiolimited.co.uk";

  const mapSrc =
    "https://www.google.com/maps?q=Unit%202,%20Beechwood%20House,%20Milnrow%20Road,%20Rochdale,%20Manchester%20OL16%202AA&output=embed";

  return (
    <>
      <SEO
        title="Contact Kinews Studio Limited"
        description="Get in touch with Kinews Studio Limited for bookings, events, studio rentals and enquiries. We are here to help you capture your moments."
        url={window.location.href}
      />
      <main className="min-h-screen bg-white text-black">
        {/* HERO */}
        <section className="relative overflow-hidden bg-black text-white py-8">
          <div className="pointer-events-none absolute inset-0 opacity-60">
            <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />
            <div className="absolute top-24 -right-24 h-80 w-80 rounded-full bg-pink-500/20 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-7xl px-5 pt-28 pb-14 md:pt-32 md:pb-16">
            <h1 className="mt-3 text-4xl md:text-6xl font-semibold tracking-tight">
              Contact
            </h1>
            <p className="mt-3 max-w-xl text-sm md:text-base text-pink-400/80 leading-relaxed">
              Reach out for bookings, questions, or custom requests.
            </p>
          </div>
        </section>

        {/* MAIN */}
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-5 py-14">
            <div className="grid gap-8 lg:grid-cols-12">
              {/* LEFT */}
              <div className="lg:col-span-5 space-y-4">
                <div className="rounded-3xl border border-black/10 bg-white p-6">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-pink-500/80">
                    Studio
                  </p>
                  <h2 className="mt-2 text-xl font-semibold">Get in touch</h2>

                  <ul className="mt-4 space-y-3 text-sm text-black/70">
                    <li>
                      <span className="font-semibold text-black">Address:</span>{" "}
                      {address}
                    </li>

                    <li>
                      <span className="font-semibold text-black">Phone:</span>{" "}
                      <a
                        className="underline underline-offset-4 hover:text-pink-500"
                        href="tel:+447486160965"
                      >
                        {phone}
                      </a>
                    </li>

                    <li>
                      <span className="font-semibold text-black">
                        Landline:
                      </span>{" "}
                      <a
                        className="underline underline-offset-4 hover:text-pink-500"
                        href="tel:01706 803 538"
                      >
                        01706 803 538
                      </a>
                    </li>

                    <li>
                      <span className="font-semibold text-black">Email:</span>{" "}
                      <a
                        className="underline underline-offset-4 hover:text-pink-500"
                        href={`mailto:${email}`}
                      >
                        {email}
                      </a>
                    </li>
                  </ul>

                  {/* SOCIAL */}
                  <div className="mt-6">
                    <p className="text-[11px] tracking-[0.2em] uppercase text-pink-500/80">
                      Social
                    </p>

                    <div className="mt-3 flex flex-wrap gap-3">
                      {[
                        "Instagram",
                        "Facebook",
                        "TikTok",
                        "YouTube",
                        "WhatsApp",
                      ].map((s) => (
                        <span
                          key={s}
                          className="rounded-2xl border border-black/10 px-4 py-2 text-sm hover:border-pink-500 hover:text-pink-500 transition cursor-pointer"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* HOURS */}
                <div className="rounded-3xl border border-black/10 bg-neutral-50 p-6">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-pink-500/80">
                    Hours
                  </p>

                  <h3 className="mt-2 text-lg font-semibold">Opening Hours</h3>

                  <ul className="mt-4 space-y-2 text-sm text-black/70">
                    {hours.map((h) => (
                      <li key={h.day} className="flex justify-between">
                        <span className="font-medium">{h.day}</span>
                        <span className="text-black/60">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* RIGHT */}
              <div className="lg:col-span-7 space-y-4">
                {/* FORM */}
                <div className="rounded-3xl border border-black/10 bg-white p-6 md:p-8">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-pink-500/80">
                    Message
                  </p>

                  <h2 className="mt-2 text-xl font-semibold">Send a message</h2>

                  <form onSubmit={onSubmit} className="mt-6 space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <input
                        className="w-full rounded-2xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-pink-500"
                        placeholder="Full name"
                        value={form.name}
                        onChange={onChange("name")}
                        required
                      />

                      <input
                        className="w-full rounded-2xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-pink-500"
                        placeholder="Email"
                        type="email"
                        value={form.email}
                        onChange={onChange("email")}
                        required
                      />
                    </div>

                    <textarea
                      className="w-full rounded-2xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-pink-500"
                      rows={6}
                      placeholder="How can we help?"
                      value={form.message}
                      onChange={onChange("message")}
                      required
                    />

                    <button
                      type="submit"
                      className="rounded-2xl bg-pink-500 px-6 py-3 text-sm font-semibold text-white hover:bg-pink-600 transition"
                    >
                      Send message
                    </button>

                    <p className="text-xs text-black/50">
                      We typically reply within 24 hours.
                    </p>
                  </form>
                </div>

                {/* MAP */}
                <div className="rounded-3xl border border-black/10 overflow-hidden">
                  <div className="px-6 py-5">
                    <p className="text-[11px] tracking-[0.2em] uppercase text-pink-500/80">
                      Location
                    </p>
                    <h3 className="mt-2 text-lg font-semibold">Find us</h3>
                  </div>

                  <div className="h-[360px] w-full">
                    <iframe
                      src={mapSrc}
                      className="h-full w-full"
                      loading="lazy"
                      title="Google Maps location of Kinews Studio"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-black text-white">
          <div className="mx-auto max-w-7xl px-5 py-14">
            <div className="rounded-3xl border border-pink-500/30 bg-white/5 p-8 md:flex md:justify-between">
              <div>
                <h2 className="text-2xl font-semibold">Ready to book?</h2>
                <p className="mt-2 text-sm text-white/70">
                  Pick a session and secure a slot.
                </p>
              </div>

              <Link
                to="/book"
                className="inline-flex items-center justify-center leading-none rounded-2xl bg-pink-500 px-6 py-3 text-sm font-semibold text-white hover:bg-pink-600 transition"
              >
                Go to Bookings
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
